/**
 * SEO Component
 *
 * Manages document head meta tags for SEO and social sharing.
 * Uses react-helmet-async for client-side meta tag management.
 */

import { Helmet } from 'react-helmet-async';

// Get API base URL for OG images
/** The same API base the RTK slices use (VITE_API_URL already ends in /api/v1). */
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'http://localhost:5173';
const APP_NAME = 'BeHeard';

export interface SEOProps {
  /** Page title (will be appended with app name) */
  title?: string;
  /** Meta description */
  description?: string;
  /** Canonical URL path (e.g., /u/johndoe) */
  canonicalPath?: string;
  /** Whether search engines should index this page */
  noIndex?: boolean;
  /** OpenGraph type (default: website) */
  ogType?: 'website' | 'profile' | 'article';
  /** OpenGraph image URL (full URL or path) */
  ogImage?: string;
  /** Twitter card type */
  twitterCard?: 'summary' | 'summary_large_image';
  /** Profile-specific: first name */
  profileFirstName?: string;
  /** Profile-specific: last name */
  profileLastName?: string;
}

/**
 * SEO component for managing document head meta tags.
 *
 * @example
 * // Basic usage
 * <SEO title="Dashboard" description="View your progress" />
 *
 * @example
 * // Public profile with OG image
 * <SEO
 *   title="John Doe"
 *   description="Communication Expert"
 *   canonicalPath="/u/johndoe"
 *   ogType="profile"
 *   ogImage={`${API_URL}/api/public-profiles/johndoe/og.png`}
 * />
 */
export function SEO({
  title,
  description,
  canonicalPath,
  noIndex = false,
  ogType = 'website',
  ogImage,
  twitterCard = 'summary_large_image',
  profileFirstName,
  profileLastName,
}: SEOProps) {
  const fullTitle = title ? `${title} — ${APP_NAME}` : APP_NAME;
  const metaDescription = description || 'AI-Powered Communication Coaching Platform';
  const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : undefined;
  const imageUrl = ogImage || `${SITE_URL}/og-default.png`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />

      {/* Robots */}
      <meta
        name="robots"
        content={noIndex ? 'noindex,nofollow' : 'index,follow'}
      />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:site_name" content={APP_NAME} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Profile-specific OG tags */}
      {ogType === 'profile' && profileFirstName && (
        <meta property="profile:first_name" content={profileFirstName} />
      )}
      {ogType === 'profile' && profileLastName && (
        <meta property="profile:last_name" content={profileLastName} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
}

/**
 * Helper to build OG image URL for public profiles
 */
export function getPublicProfileOgImageUrl(slug: string, apiBase: string = API_BASE_URL): string {
  // GET /api/v1/public-profiles/:slug/og.png. It used to append "/api/…" to a
  // base that already ends in /api/v1 (→ /api/v1/api/…, no route) and to
  // default to http://localhost:3001 in a build without VITE_API_URL. An
  // og:image must be absolute, so a relative base is resolved on this origin.
  const base = apiBase.replace(/\/+$/, '');
  const absolute = /^https?:\/\//.test(base)
    ? base
    : `${typeof window !== 'undefined' ? window.location.origin : ''}${base.startsWith('/') ? '' : '/'}${base}`;
  return `${absolute}/public-profiles/${encodeURIComponent(slug)}/og.png`;
}

export default SEO;
